import type { FileForProcessing } from '../Sample/ISample.js'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.js'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.js'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.js'
import type { SampleConfigurationSwitchRejectionKey } from '../../Generated/SampleConfigurationSwitchRejection.js'

export type ValidationIssue =
  | NoChangedSamples
  | SampleInvalid
  | NoItems
  | MissingFile
  | AdditionalFile
  | StorageBackendError
  | EmptyPool
  | InvalidMultiSample
  | InvalidMergedSample
  | InvalidTrimmedSample
  | SampleNotMerged
  | SampleNotTrimmed
  | MissingReverseFile
  | MisalignedFileReferences
  | InvalidTargetRegion
  | InvalidProcessingTypeTaxonomyCombination
  | MixedSequencingPlatforms
  | UnsupportedSampleConfigurationForPlatform
  | UnsupportedProcessingTypeForPlatform
  | SampleConfigurationSwitchRefused
  | OrderAlreadySubmitted
  | PairedSamplesOnSingleEndPlatform
  | ReverseFileAttachRefused

export interface SampleInvalid {
  type: 'sample-invalid'
  item: FileForProcessing
  issue: string
}

export interface NoChangedSamples {
  type: 'no-changed-samples'
}

export interface NoItems {
  type: 'no-items'
}

export interface MissingFile {
  type: 'missing-file'
  item?: FileForProcessing | null
  /** The storage object key (contains ids, not meaningful to a user). */
  filename: string
  /** The name the user uploaded the file under — use this in anything user-facing. */
  originalFileName?: string | null
}

export interface AdditionalFile {
  type: 'additional-file'
  filename: string
}

export interface StorageBackendError {
  type: 'storage-error'
}

export interface EmptyPool {
  type: 'empty-pool'
}

export interface InvalidMultiSample {
  type: 'invalid-multi-sample'
  item: FileForProcessing
  issue: string
}

export interface InvalidMergedSample {
  type: 'invalid-merged-sample'
  item: FileForProcessing
  issue: string
}

export interface InvalidTrimmedSample {
  type: 'invalid-trimmed-sample'
  item: FileForProcessing
  issue: string
}

export interface SampleNotMerged {
  type: 'sample-not-merged'
  item: FileForProcessing
  issue: string
}

export interface SampleNotTrimmed {
  type: 'sample-not-trimmed'
  item: FileForProcessing
  issue: string
}

export interface MissingReverseFile {
  type: 'missing-reverse-file'
  item: FileForProcessing
  issue: string
}

export interface MisalignedFileReferences {
  type: 'misaligned-file-references'
  itemA: FileForProcessing
  itemB: FileForProcessing
}

export interface InvalidTargetRegion {
  type: 'invalid-target-region'
  issue: string
}

export interface InvalidProcessingTypeTaxonomyCombination {
  type: 'invalid-processing-type-taxonomy-combination'
  issue: string
}

export interface MixedSequencingPlatforms {
  type: 'mixed-sequencing-platforms'
  platforms: SequencingPlatformKey[]
  issue: string
}

export interface UnsupportedSampleConfigurationForPlatform {
  type: 'unsupported-sample-configuration-for-platform'
  platform: SequencingPlatformKey
  sampleConfiguration: SampleConfigurationTypeKey
  issue: string
}

export interface UnsupportedProcessingTypeForPlatform {
  type: 'unsupported-processing-type-for-platform'
  platform: SequencingPlatformKey
  processingType: ProcessingTypeKey
  issue: string
}

export interface SampleConfigurationSwitchRefused {
  type: 'sample-configuration-switch-refused'
  from?: SampleConfigurationTypeKey | null
  to?: SampleConfigurationTypeKey | null
  reason: SampleConfigurationSwitchRejectionKey
  issue: string
}

export interface OrderAlreadySubmitted {
  type: 'order-already-submitted'
  issue: string
}

/** `sampleNames` follow `sampleIds`; the backend's `issue` already names them. */
export interface PairedSamplesOnSingleEndPlatform {
  type: 'paired-samples-on-single-end-platform'
  platform: SequencingPlatformKey
  sampleIds: string[]
  sampleNames: string[]
  issue: string
}

export interface ReverseFileAttachRefused {
  type: 'reverse-file-attach-refused'
  sampleId: string
  fileId: string
  issue: string
}

export function isSampleInvalid(issue: ValidationIssue): issue is SampleInvalid {
  return issue.type === 'sample-invalid'
}

export function isNoItems(issue: ValidationIssue): issue is NoItems {
  return issue.type === 'no-items'
}

export function isMissingFile(issue: ValidationIssue): issue is MissingFile {
  return issue.type === 'missing-file'
}

export function isAdditionalFile(issue: ValidationIssue): issue is AdditionalFile {
  return issue.type === 'additional-file'
}

export function isStorageBackendError(issue: ValidationIssue): issue is StorageBackendError {
  return issue.type === 'storage-error'
}

export function isEmptyPool(issue: ValidationIssue): issue is EmptyPool {
  return issue.type === 'empty-pool'
}

export function isInvalidMultiSample(issue: ValidationIssue): issue is InvalidMultiSample {
  return issue.type === 'invalid-multi-sample'
}

export function isInvalidMergedSample(issue: ValidationIssue): issue is InvalidMergedSample {
  return issue.type === 'invalid-merged-sample'
}

export function isInvalidTrimmedSample(issue: ValidationIssue): issue is InvalidTrimmedSample {
  return issue.type === 'invalid-trimmed-sample'
}

export function isSampleNotMerged(issue: ValidationIssue): issue is SampleNotMerged {
  return issue.type === 'sample-not-merged'
}

export function isSampleNotTrimmed(issue: ValidationIssue): issue is SampleNotTrimmed {
  return issue.type === 'sample-not-trimmed'
}

export function isMissingReverseFile(issue: ValidationIssue): issue is MissingReverseFile {
  return issue.type === 'missing-reverse-file'
}

export function isMisalignedFileReferences(issue: ValidationIssue): issue is MisalignedFileReferences {
  return issue.type === 'misaligned-file-references'
}

export function isNoChangedSamples(issue: ValidationIssue): issue is NoChangedSamples {
  return issue.type === 'no-changed-samples'
}

export function isInvalidTargetRegion(issue: ValidationIssue): issue is InvalidTargetRegion {
  return issue.type === 'invalid-target-region'
}

export function isInvalidProcessingTypeTaxonomyCombination(
  issue: ValidationIssue
): issue is InvalidProcessingTypeTaxonomyCombination {
  return issue.type === 'invalid-processing-type-taxonomy-combination'
}

export function isMixedSequencingPlatforms(issue: ValidationIssue): issue is MixedSequencingPlatforms {
  return issue.type === 'mixed-sequencing-platforms'
}

export function isUnsupportedSampleConfigurationForPlatform(
  issue: ValidationIssue
): issue is UnsupportedSampleConfigurationForPlatform {
  return issue.type === 'unsupported-sample-configuration-for-platform'
}

export function isUnsupportedProcessingTypeForPlatform(issue: ValidationIssue): issue is UnsupportedProcessingTypeForPlatform {
  return issue.type === 'unsupported-processing-type-for-platform'
}

export function isSampleConfigurationSwitchRefused(issue: ValidationIssue): issue is SampleConfigurationSwitchRefused {
  return issue.type === 'sample-configuration-switch-refused'
}

export function isOrderAlreadySubmitted(issue: ValidationIssue): issue is OrderAlreadySubmitted {
  return issue.type === 'order-already-submitted'
}

export function isPairedSamplesOnSingleEndPlatform(issue: ValidationIssue): issue is PairedSamplesOnSingleEndPlatform {
  return issue.type === 'paired-samples-on-single-end-platform'
}

export function isReverseFileAttachRefused(issue: ValidationIssue): issue is ReverseFileAttachRefused {
  return issue.type === 'reverse-file-attach-refused'
}
